<script setup>
import { computed } from "vue";
import MSheet from "@/components/m/MSheet.vue";
import MIcon from "@/components/m/MIcon.vue";
import menuAside from "@/menuAside.js";

/* "More" navigation — derived from the same menuAside.js the desktop sidebar
   uses, so the two can't drift. The bottom-tab bar already covers the primary
   screens, so those routes are excluded here; everything else the sidebar
   exposes is rendered, grouped. Items route to the real (responsive) view via
   the parent's navigate handler. */
const props = defineProps({
  open: { type: Boolean, default: false },
  isSuperuser: { type: Boolean, default: false },
  isDark: { type: Boolean, default: false },
});
const emit = defineEmits(["close", "navigate", "toggle-theme", "logout"]);

// Routes owned by the bottom tab bar — not repeated in "More".
const TAB_ROUTES = new Set([
  "/",
  "/reviews",
  "/topics",
  "/entities",
  "/competitors",
]);
// Map sidebar routes onto the mobile stroke-icon set (falls back to a dot).
const ICON_FOR = {
  "/socials": "social",
  "/social-topics": "topics",
  "/social-entities": "entities",
  "/social-inbox": "inbox",
  "/analytics": "analytics",
  "/configuration/channels": "globe",
  "/configuration/locations": "target",
  "/team": "profile",
  "/backend/admin": "shield",
  "/profile": "profile",
};
const iconFor = (to) => ICON_FOR[to] || "dots";

const sections = computed(() => {
  const workspace = [];
  const groups = [];
  for (const item of menuAside) {
    if (item.superuser && !props.isSuperuser) continue;
    // Nested group (e.g. Configuration → Channels/Locations/Team).
    if (item.menu) {
      groups.push({
        label: item.label,
        items: item.menu.map((c) => ({
          to: c.to,
          label: c.label,
          icon: iconFor(c.to),
        })),
      });
      continue;
    }
    if (!item.to || TAB_ROUTES.has(item.to)) continue;
    const entry = { to: item.to, label: item.label, icon: iconFor(item.to) };
    if (item.label === "Admin") groups.push({ label: "Admin", items: [entry] });
    else workspace.push(entry);
  }
  const result = [];
  if (workspace.length) result.push({ label: "Workspace", items: workspace });
  result.push(...groups);
  result.push({
    label: "Account",
    items: [{ to: "/profile", label: "Profile", icon: "profile" }],
  });
  return result;
});

const pick = (to) => {
  emit("navigate", to);
  emit("close");
};
</script>

<template>
  <MSheet :open="open" label="More navigation" @close="emit('close')">
    <div
      style="display: flex; align-items: center; gap: 10px; margin: 2px 0 14px"
    >
      <div style="font-size: 19px; font-weight: 800; letter-spacing: -0.02em">
        More
      </div>
      <span class="btm-muted" style="margin-left: auto">All pages</span>
    </div>

    <template v-for="section in sections" :key="section.label">
      <div class="btm-eyebrow" style="margin: 4px 2px 8px">
        {{ section.label }}
      </div>
      <div
        style="
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        "
      >
        <button
          v-for="item in section.items"
          :key="item.to"
          type="button"
          style="
            display: flex;
            align-items: center;
            gap: 13px;
            padding: 13px 14px;
            border-radius: 14px;
            border: 1px solid var(--border);
            background: var(--surface);
            cursor: pointer;
            text-align: left;
            font-family: var(--ui);
            width: 100%;
          "
          @click="pick(item.to)"
        >
          <span
            style="
              width: 36px;
              height: 36px;
              border-radius: 11px;
              flex: 0 0 auto;
              display: grid;
              place-items: center;
              background: var(--surface-2);
              color: var(--text-2);
            "
          >
            <MIcon :name="item.icon" :size="18" />
          </span>
          <span
            style="flex: 1; min-width: 0; font-weight: 700; font-size: 14.5px"
          >
            {{ item.label }}
          </span>
          <MIcon name="chevright" :size="17" style="color: var(--text-3)" />
        </button>
      </div>
    </template>

    <div
      style="
        display: flex;
        gap: 9px;
        margin-top: 2px;
        padding-top: 14px;
        border-top: 1px solid var(--border);
      "
    >
      <button
        class="btm-btn btm-btn-ghost"
        type="button"
        @click="emit('toggle-theme')"
      >
        <MIcon :name="isDark ? 'sun' : 'moon'" :size="16" />
        {{ isDark ? "Light mode" : "Dark mode" }}
      </button>
      <button
        class="btm-btn btm-btn-ghost"
        type="button"
        style="color: var(--neg)"
        @click="emit('logout')"
      >
        <MIcon name="logout" :size="16" />Log out
      </button>
    </div>
  </MSheet>
</template>
